// Shared wash packages configuration with July special pricing.

export const REGULAR_WASH_PRICES = {
  'BASIC WASH': 50,
  'COMFORT WASH': 55,
  'PREMIUM WASH': 60,
};

/** July promotional prices (Basic $27, Comfort $29, Premium $32). */
export const JULY_SPECIAL_PRICES = {
  'BASIC WASH': 27,
  'COMFORT WASH': 29,
  'PREMIUM WASH': 32,
};

export const JULY_SPECIAL_NOTICE =
  'JULY SPECIAL: Basic $27 • Comfort $29 • Premium $32 — limited-time offer this month only!';

/** Active when the given date falls in July (appointment day or “today” for display). */
export function isJulySpecialActive(date = new Date()) {
  return date.getMonth() === 6; // 0-indexed: July = 6
}

/** Parse YYYY-MM-DD (or Date) as a local calendar day for pricing. */
export function parseAppointmentDate(dateInput) {
  if (!dateInput) return new Date();
  if (dateInput instanceof Date) return dateInput;
  const str = String(dateInput);
  if (str.includes('-')) {
    const [y, m, d] = str.split('-').map(Number);
    if (y && m && d) return new Date(y, m - 1, d);
  }
  if (str.includes('/')) {
    const [d, m, y] = str.split('/').map(Number);
    if (y && m && d) return new Date(y, m - 1, d);
  }
  return new Date(dateInput);
}

/**
 * Wash package price for a service.
 * Pass the appointment date so July special applies only to July timeslots.
 * Omit date (or pass today) for homepage/catalog display.
 */
export function getWashPriceAmount(serviceName, date = new Date()) {
  const key = (serviceName || '').toUpperCase();
  const day = parseAppointmentDate(date);
  if (isJulySpecialActive(day)) {
    return JULY_SPECIAL_PRICES[key] ?? REGULAR_WASH_PRICES[key] ?? 0;
  }
  return REGULAR_WASH_PRICES[key] ?? 0;
}

const packageDefs = [
  {
    id: 'basic-wash',
    name: 'BASIC WASH',
    description: 'ON THE GO',
    background: '#f3f4f6',
    textColor: '#1f2937',
    icon: '🚗',
    features: [
      { name: 'Under-chasis Spray', included: true },
      { name: 'Wipe-free Foam', included: true },
      { name: 'Spray Foam', included: false },
      { name: 'Waterfall Foam', included: false },
      { name: 'Water Wax', included: true },
      { name: 'Tyre Shine', included: true },
    ],
  },
  {
    id: 'comfort-wash',
    name: 'COMFORT WASH',
    description: 'CLEAN',
    background: '#3c3c3c',
    textColor: '#fb923c',
    icon: '🫧',
    features: [
      { name: 'Under-chasis Spray', included: true },
      { name: 'Wipe-free Foam', included: true },
      { name: 'Spray Foam', included: true },
      { name: 'Waterfall Foam', included: false },
      { name: 'Water Wax', included: true },
      { name: 'Tyre Shine', included: true },
    ],
  },
  {
    id: 'premium-wash',
    name: 'PREMIUM WASH',
    description: 'EXTRA CLEAN',
    background: '#fb923c',
    textColor: 'white',
    icon: '💎',
    features: [
      { name: 'Under-chasis Spray', included: true },
      { name: 'Wipe-free Foam', included: true },
      { name: 'Spray Foam', included: true },
      { name: 'Waterfall Foam', included: true },
      { name: 'Water Wax', included: true },
      { name: 'Tyre Shine', included: true },
    ],
  },
];

export function getWashPackages(date = new Date()) {
  const onSale = isJulySpecialActive(date);
  return packageDefs.map((pkg) => {
    const amount = getWashPriceAmount(pkg.name, date);
    const regular = REGULAR_WASH_PRICES[pkg.name];
    return {
      ...pkg,
      price: `$${amount}`,
      regularPrice: `$${regular}`,
      isOnSale: onSale && amount < regular,
    };
  });
}

/** Snapshot for callers that still import a static list; prefer getWashPackages(). */
export const washPackages = getWashPackages();

export const getPackageById = (id) => getWashPackages().find((pkg) => pkg.id === id);

export const getPackageByName = (name) => getWashPackages().find((pkg) => pkg.name === name);
